import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import TurndownService from "turndown";

export function getKeyByValue(object, value) {
  return Object.keys(object).find((key) => object[key] === value);
}

export const delay = (n) => new Promise((r) => setTimeout(r, n * 1000));

export const getNonEmpty = (field) => {
  const defaultLanguage = "es";
  if (!field || !Object.keys(field)?.length) return "----";
  if (field[defaultLanguage] && field[defaultLanguage] !== "")
    return field[defaultLanguage];
  for (const lang of Object.keys(field)) {
    if (field[lang] && field[lang] !== "") return field[lang];
  }
  return "----";
};

export const checkIsEmpty = (field) => {
  if (field === null || field === "" || field === " ") return true;
  return Object.values(field).every((x) => x === null || x === "" || x === " ");
};

export const getFileType = (fileName) => {
  if (!fileName) return "unknown";
  const ext = fileName.split(".").pop();
  switch (ext) {
    case "pdf":
      return "file";
    case "m4a":
      return "audio";
    case "mp3":
      return "audio";
    case "mpa":
      return "audio";
    case "wma":
      return "audio";
    case "aif":
      return "audio";
    case "cda":
      return "audio";
    case "mid":
      return "audio";
    case "midi":
      return "audio";
    case "ogg":
      return "audio";
    case "wav":
      return "audio";
    case "wpl":
      return "audio";
    case "jpg":
      return "image";
    case "jpeg":
      return "image";
    case "png":
      return "image";
    case "svg":
      return "image";
    case "gif":
      return "image";
    case "avi":
      return "video";
    case "m4v":
      return "video";
    case "mkw":
      return "video";
    case "mp4":
      return "video";
    case "wmv":
      return "video";
    case "mov":
      return "video";
    default:
      return "unknown";
  }
};

export function normalizeString(str) {
  if (!str) return "";
  return str
    .normalize("NFD")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/(?:^|\s)\S/g, (char) => char.toUpperCase());
}

export function formatInstallationTime(minutes) {
  if (!minutes) return 0;
  const hours = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");

  const remainingMinutes = minutes % 60;

  const formattedMinutes = remainingMinutes.toString().padStart(2, "0");

  return `${hours}:${formattedMinutes}`;
}

export function calculateMinutesFromTime(time) {
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    minutes < 0 ||
    minutes >= 60
  ) {
    return null;
  }

  return hours * 60 + minutes;
}

function formatHtml(html) {
  return html
    .replace(/<br\s*\/?>/gi, "\n") // Convierte <br> en saltos de línea
    .replace(/<b>(.*?)<\/b>/gi, "*$1*") // Convierte <b> en texto con asteriscos (simulando negritas)
    .replace(/<[^>]*>/g, ""); // Elimina las demás etiquetas
}

function calcularAnchoTexto(doc, texto) {
  const textSize = doc.getTextDimensions(texto);
  return textSize.w + 4;
}

function formatNumber(num) {
  return num
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}
function checkPageBreak(doc, yOffset, margin = 20) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (yOffset + margin >= pageHeight) {
    doc.addPage();
    return 20; // Reiniciar yOffset al margen superior de la nueva página
  }
  return yOffset;
}

export function generatePDF(oferta) {
  const doc = new jsPDF();

  // Cabezera
  doc.setFont("Arial", "bold");
  doc.setFontSize(16);
  doc.text(`PRESUPUESTO Nº ${oferta.num_oferta}`, 20, 20);

  doc.setFont("Arial", "normal");
  doc.setFontSize(12);
  doc.text(` Revision  ${oferta.revision_num}`, 100, 20);
  doc.text(`Peticionario/Cliente: ${oferta.cliente_nombre}`, 20, 30);
  doc.text(`Localización/Obra: ${oferta.localizacion}`, 20, 40);
  doc.text(`Referencia: ${oferta.referencia}`, 20, 50);

  // Capítulos
  let yOffset = 60;
  const marginLeft = 20;
  const marginRight = 12;
  const rectWidth = doc.internal.pageSize.getWidth() - marginLeft - marginRight;
  const rectHeight = 8;

  // Dibujar fondo rojo para el título "MEDICIONES: INSTALACIONES"
  doc.setFillColor(255, 0, 0);
  const xx = marginLeft;
  const yy = yOffset - 5;
  doc.rect(xx, yy, rectWidth, rectHeight, "F");

  // Estilo del texto del título
  doc.setTextColor(255, 255, 255);
  doc.setFont("Arial", "bold");
  doc.text("MEDICIONES:     INSTALACIONES", marginLeft + 5, yOffset);
  yOffset += 10;

  const turndownService = new TurndownService();
  const cabeceraTexto = turndownService.turndown(oferta.cabecera_descripcion);

  const splitDescripcion = doc.splitTextToSize(cabeceraTexto, rectWidth);

  doc.setTextColor(0, 0, 0);
  doc.setFont("Arial", "normal");
  doc.text(splitDescripcion, 25, yOffset);
  yOffset += splitDescripcion.length * 6;
  yOffset = checkPageBreak(doc, yOffset);

  oferta.capitulos.forEach((capitulo) => {
    doc.setFillColor(255, 0, 0);
    const rectY = yOffset - 5;
    const rectX = marginLeft;
    doc.rect(rectX, rectY, rectWidth, rectHeight, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("Arial", "bold");
    const capitulo_inex = capitulo.ordinal + 1;
    doc.text(
      `Mediciones:    ${capitulo_inex}     ${capitulo.partida_categoria.descripcion} `,
      25,
      yOffset
    );
    yOffset += 10;

    //Partida dentro de capitulos
    capitulo.partidas.forEach((partida) => {
      yOffset = checkPageBreak(doc, yOffset);
      doc.setFont("Arial", "normal");
      const anchoPrecio = calcularAnchoTexto(
        doc,
        `${formatNumber(partida.precio_unitario)} €`
      );
      const anchoTotal = calcularAnchoTexto(
        doc,
        `${formatNumber(partida.precio_total)} €`
      );
      autoTable(doc, {
        startY: yOffset,
        head: [["Pos.", "Ud.", "Descripción", "Med.", "Precio", "Total"]],
        body: [
          [
            `${capitulo_inex}.${partida.ordinal + 1}`,
            `${partida.tipo_unidad || "Ud."}`,
            `${partida.descripcion}\n${formatHtml(partida.concepto)}`,
            partida.unidades,
            `${formatNumber(partida.precio_unitario)} €`,
            `${formatNumber(partida.precio_total)} €`,
          ],
        ],
        theme: "grid",
        margin: { top: 10, bottom: 10, left: 20, right: 12 },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [200, 200, 200],
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center" },
          1: { cellWidth: 15, halign: "center" },
          2: { cellWidth: "auto", halign: "left" },
          3: { cellWidth: 15, halign: "center" },
          4: { cellWidth: 25, halign: "center" },
          5: { cellWidth: 25, halign: "center" },
        },
      });

      yOffset = doc.lastAutoTable.finalY + 10;
      yOffset = checkPageBreak(doc, yOffset);

      yOffset = doc.lastAutoTable.finalY + 10;
    });
  });

  // Total Presupuesto
  // Definir las posiciones iniciales
  const marginLefttext = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const rightAlignX = pageWidth - marginLefttext;

  // Formatear valores numéricos en formato de moneda
  const totalPresupuesto = formatNumber(oferta.calc_total_pvp) + " €";
  const valorIVA = (oferta.valor_IVA * 100).toFixed(0) + "%";
  const aux = oferta.total_PVP_IVA - oferta.calc_total_pvp;
  const valorIVAEuros = formatNumber(aux) + " €";
  const totalConIVA = formatNumber(oferta.total_PVP_IVA) + " €";

  // Asegurar alineación correcta
  const lineSpacing = 8;
  yOffset += 10;
  yOffset = checkPageBreak(doc, yOffset);

  // Línea 1: INSTALACIONES
  doc.setFillColor(255, 0, 0);
  let rectY = yOffset - 5;
  const rectX = marginLeft;
  doc.rect(rectX, rectY, rectWidth, rectHeight, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("Arial", "bold");
  doc.text("TOTAL SIN IVA", marginLeft, yOffset);
  doc.text(
    totalPresupuesto,
    rightAlignX - doc.getTextWidth(totalPresupuesto),
    yOffset
  );

  // Línea 2: Tipo de IVA
  yOffset += lineSpacing;

  doc.setTextColor(0, 0, 0);
  doc.setFont("Arial", "normal");
  doc.text("    Tipo de IVA APLICABLE ………………………………………………", marginLeft, yOffset);
  doc.text(
    valorIVA,
    rightAlignX -
      doc.getTextWidth(valorIVA) -
      doc.getTextWidth(valorIVAEuros) -
      10,
    yOffset
  );
  doc.text(
    valorIVAEuros,
    rightAlignX - doc.getTextWidth(valorIVAEuros),
    yOffset
  );

  // Línea 3: TOTAL IVA INCLUIDO
  yOffset += lineSpacing;
  doc.setFillColor(255, 0, 0);
  rectY = yOffset - 5;
  doc.rect(rectX, rectY, rectWidth, rectHeight, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("Arial", "bold");
  doc.text("TOTAL IVA INCLUIDO  ………………………………………………..", marginLeft, yOffset);
  doc.text(totalConIVA, rightAlignX - doc.getTextWidth(totalConIVA), yOffset);

  doc.addPage();
  yOffset = 20;

  // Dibujar fondo rojo para el título "MEDICIONES: INSTALACIONES"
  doc.setFillColor(255, 0, 0);
  const xx2 = marginLeft;
  const yy2 = yOffset - 5;
  doc.rect(xx2, yy2, rectWidth, rectHeight, "F");

  // Estilo del texto del título
  doc.setTextColor(255, 255, 255);
  doc.setFont("Arial", "bold");
  doc.text(
    "                   CONDICIONES DE GARANTIAS y VENTA",
    marginLeft + 5,
    yOffset
  );
  yOffset += 10;

  const turndownService2 = new TurndownService();
  const cabeceraTexto2 = turndownService2.turndown(
    oferta.pie_condiciones_venta
  );

  const splitDescripcion2 = doc.splitTextToSize(cabeceraTexto2, rectWidth);

  doc.setTextColor(0, 0, 0);
  doc.setFont("Arial", "normal");
  doc.text(splitDescripcion2, 25, yOffset);
  yOffset += splitDescripcion2.length * 5;

  doc.setFillColor(255, 0, 0);
  rectY = yOffset - 5;
  doc.rect(rectX, rectY, rectWidth, rectHeight, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("Arial", "bold");
  doc.text("        INSTALACIONES              ", marginLeft, yOffset);
  doc.text(totalConIVA, rightAlignX - doc.getTextWidth(totalConIVA), yOffset);

  // Guardar el archivo PDF
  doc.save("presupuesto.pdf");
}
